import { type RefObject, useCallback, useEffect, useRef } from 'react';
import type { Guide } from './types';
import { getStepNumberForTime, isTimestampedVideoStep, loadYoutubeApi } from './video';

interface SeekOptions {
   play?: boolean;
}

// Minimal shape of the YouTube IFrame player we command.
interface YtPlayer {
   seekTo(seconds: number, allowSeekAhead: boolean): void;
   playVideo(): void;
   getCurrentTime(): number;
   destroy(): void;
}

interface Params {
   guide: Guide;
   // The persistent <iframe> (YouTube) or <video> (self-hosted) media element.
   mediaRef: RefObject<HTMLIFrameElement | HTMLVideoElement | null>;
   stepIndex: number;
   // Called when playback crosses into a different step, so the visible step
   // follows the video. Must be identity-stable per the caller (a setState).
   onVideoStep: (stepNumber: number) => void;
}

/**
 * Two-way sync between a chaptered video guide's persistent player and the
 * visible step (the React port of VideoGuideController in embed.v5.steps.js):
 *
 * - video → step: a poll (YouTube) or `timeupdate` (self-hosted) advances the
 *   visible step to the chapter the playhead is in.
 * - step → video: manual navigation seeks the player to the step's start, with
 *   a pending-seek guard so an in-flight seek isn't reverted by the poller.
 *
 * All live state is held in refs so the once-mounted player and its listeners
 * never need to be rebuilt (rebuilding would reload the video).
 */
export function useGuideVideoSync({ guide, mediaRef, stepIndex, onVideoStep }: Params) {
   const isYoutube = guide.video_info?.type === 'youtube';

   const playerRef = useRef<YtPlayer | HTMLVideoElement | null>(null);
   const readyRef = useRef(false);
   const visibleStepRef = useRef(stepIndex);
   // The step the poller last pushed up, so the stepIndex effect can tell a
   // video-driven change (don't seek) from a user navigation (do seek).
   const videoReportedStepRef = useRef<number | null>(null);
   const pendingSeekRef = useRef<number | null>(null);
   const pendingTimerRef = useRef<number | undefined>(undefined);
   const pollRef = useRef<number | undefined>(undefined);
   // Latest props mirrored into refs so the once-mounted player's listeners read
   // current values without being rebuilt. Refreshed after commit (below).
   const onVideoStepRef = useRef(onVideoStep);
   const guideRef = useRef(guide);
   const stepIndexRef = useRef(stepIndex);
   useEffect(() => {
      onVideoStepRef.current = onVideoStep;
      guideRef.current = guide;
      stepIndexRef.current = stepIndex;
   });

   const stepStartTime = useCallback((stepNumber: number): number => {
      const step = guideRef.current.steps[stepNumber - 1];
      return step?.videoTimestamp ? step.videoTimestamp.startTime : 0;
   }, []);

   const getCurrentTime = useCallback((): number | null => {
      const player = playerRef.current;
      if (!player) {
         return null;
      }
      if (isYtPlayer(player)) {
         return player.getCurrentTime();
      }
      return Number.isNaN(player.currentTime) ? null : player.currentTime;
   }, []);

   const seekTo = useCallback((time: number, options: SeekOptions = {}) => {
      const player = playerRef.current;
      if (!player) {
         return;
      }
      if (isYtPlayer(player)) {
         player.seekTo(time, true);
         if (options.play) {
            player.playVideo();
         }
         return;
      }
      player.currentTime = time;
      if (options.play) {
         void player.play();
      }
   }, []);

   const seekToStep = useCallback(
      (stepNumber: number, options: SeekOptions = {}) => {
         if (!isTimestampedVideoStep(guideRef.current, stepNumber)) {
            return;
         }
         // A manual jump owns the visible step: mark it pending so the poller
         // ignores in-flight positions until playback reaches it, with a timeout
         // safety net so a seek that never lands can't wedge the poller.
         visibleStepRef.current = stepNumber;
         pendingSeekRef.current = stepNumber;
         window.clearTimeout(pendingTimerRef.current);
         pendingTimerRef.current = window.setTimeout(() => {
            pendingSeekRef.current = null;
         }, 3000);
         seekTo(stepStartTime(stepNumber), options);
      },
      [seekTo, stepStartTime]
   );

   const syncStepToCurrentTime = useCallback(() => {
      if (!readyRef.current) {
         return;
      }
      const time = getCurrentTime();
      if (time === null) {
         return;
      }
      const stepNumber = getStepNumberForTime(guideRef.current.steps, time);
      if (!stepNumber) {
         return;
      }
      if (pendingSeekRef.current !== null) {
         if (stepNumber === pendingSeekRef.current) {
            pendingSeekRef.current = null;
            window.clearTimeout(pendingTimerRef.current);
         }
         return;
      }
      if (stepNumber === visibleStepRef.current) {
         return;
      }
      visibleStepRef.current = stepNumber;
      videoReportedStepRef.current = stepNumber;
      onVideoStepRef.current(stepNumber);
   }, [getCurrentTime]);

   // Create the player once and wire the video → step sync. Deps are all
   // identity-stable, so this runs a single time for the player's lifetime.
   useEffect(() => {
      const media = mediaRef.current;
      if (!media) {
         return;
      }

      if (isYoutube) {
         let cancelled = false;
         void loadYoutubeApi().then(() => {
            const YT = (window as unknown as { YT?: { Player: new (...a: unknown[]) => YtPlayer } })
               .YT;
            if (cancelled || !YT?.Player || !document.body.contains(media)) {
               return;
            }
            playerRef.current = new YT.Player(media, {
               events: {
                  onReady: (event: { target: YtPlayer }) => {
                     event.target.seekTo(stepStartTime(visibleStepRef.current), true);
                     readyRef.current = true;
                     window.clearInterval(pollRef.current);
                     pollRef.current = window.setInterval(syncStepToCurrentTime, 500);
                  },
               },
            });
         });
         return () => {
            cancelled = true;
            window.clearInterval(pollRef.current);
            window.clearTimeout(pendingTimerRef.current);
            const player = playerRef.current;
            if (player && isYtPlayer(player)) {
               player.destroy();
            }
            playerRef.current = null;
            readyRef.current = false;
         };
      }

      const video = media as HTMLVideoElement;
      playerRef.current = video;
      video.addEventListener('timeupdate', syncStepToCurrentTime);
      const setInitialTime = () => {
         video.currentTime = stepStartTime(visibleStepRef.current);
         readyRef.current = true;
      };
      if (video.readyState >= 1) {
         setInitialTime();
      } else {
         video.addEventListener('loadedmetadata', setInitialTime, { once: true });
      }
      return () => {
         window.clearTimeout(pendingTimerRef.current);
         video.removeEventListener('timeupdate', syncStepToCurrentTime);
         playerRef.current = null;
         readyRef.current = false;
      };
   }, [isYoutube, mediaRef, stepStartTime, syncStepToCurrentTime]);

   // Follow user navigation: seek to the step's start, but skip when the change
   // was the poller pushing the step up (that step is already on screen).
   useEffect(() => {
      if (videoReportedStepRef.current === stepIndex) {
         videoReportedStepRef.current = null;
         visibleStepRef.current = stepIndex;
         return;
      }
      seekToStep(stepIndex, { play: false });
   }, [stepIndex, seekToStep]);

   return {
      skipToCurrentStep: useCallback(
         () => seekToStep(stepIndexRef.current, { play: true }),
         [seekToStep]
      ),
   };
}

function isYtPlayer(player: YtPlayer | HTMLVideoElement): player is YtPlayer {
   return typeof (player as YtPlayer).getCurrentTime === 'function';
}
